import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import UsageBadge from './UsageBadge';
import { FeedbackModal } from './FeedbackModal';

interface HeaderProps {
  activeTab?: 'intelligence' | 'pipeline';
  onTabChange?: (tab: 'intelligence' | 'pipeline') => void;
}

const Header: React.FC<HeaderProps> = ({ activeTab = 'intelligence', onTabChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuthStore();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleTabClick = (tab: 'intelligence' | 'pipeline') => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      if (tab === 'intelligence') {
        navigate('/search');
      } else {
        navigate('/leads');
      }
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
    <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-[0_8px_30px_rgb(79,70,229,0.3)] transform -rotate-3 hover:rotate-0 transition-transform cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tighter">
              Lead<span className="text-indigo-600">Pilot</span>
            </h1>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.25em] leading-none">
              Your Lead Generation Co-Pilot
            </p>
          </div>
        </div>
        
        <nav className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
          <button 
            onClick={() => handleTabClick('intelligence')}
            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
              activeTab === 'intelligence' || location.pathname === '/search' 
                ? 'bg-white shadow-sm text-indigo-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Intelligence Hub
          </button>
          <button 
            onClick={() => handleTabClick('pipeline')}
            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
              activeTab === 'pipeline' || location.pathname === '/leads' 
                ? 'bg-white shadow-sm text-indigo-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Pipeline
          </button>
        </nav>

        <UsageBadge />

        <div className="flex items-center gap-4">
          <button
            onClick={() => setFeedbackOpen(true)}
            className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors flex items-center gap-1.5"
            title="Send feedback"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Feedback
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
          >
            Dashboard
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
          >
            Profile
          </button>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    </header>
    <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
};

export default Header;
